import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../api/authAPI';
import { pointsAPI } from '../../api/pointsAPI';
import { getUserProfile, logout } from '../../store/slices/authSlice';
import LocationPicker from '../../components/common/LocationPicker';
import { logger } from '../../utils/logger';
import compressProfileImage from '../../utils/compressProfileImage';
import { splitUserName } from '../../utils/userName';

const profileFormFromUser = (user) => {
  const { firstName, lastName } = splitUserName(user);
  return {
    username: user?.username || '',
    first_name: firstName,
    last_name: lastName,
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    shipping_address: user?.shipping_address || '',
    latitude: user?.latitude ?? undefined,
    longitude: user?.longitude ?? undefined,
  };
};

const COORDINATE_ADDRESS_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

const parseCoordinateAddress = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(COORDINATE_ADDRESS_REGEX);
  if (!match) return null;
  return { latitude: Number(match[1]), longitude: Number(match[2]) };
};

const reverseGeocodeAddress = async ({ latitude, longitude }, apiKey) => {
  if (!apiKey) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
      `${latitude},${longitude}`
    )}&key=${encodeURIComponent(apiKey)}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data?.status === 'OK' && data.results?.[0]?.formatted_address) {
      return data.results[0].formatted_address;
    }
  } catch (_error) {
    // Fallback handled by caller.
  }
  return null;
};

const ProfileContainer = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 2.5rem 1rem 3rem;
  min-height: calc(100vh - 120px);
`;

const ProfileCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 1.2rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.7rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PasswordRequirements = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;

  ul {
    margin: 0.5rem 0 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.82rem 0.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.6rem;
  font-size: 1rem;
  background-color: #f8fafc;
  color: #0f172a;
  
  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const TextArea = styled.textarea`
  padding: 0.82rem 0.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.6rem;
  font-size: 1rem;
  background-color: #f8fafc;
  color: #0f172a;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const Button = styled.button`
  padding: 0.78rem 1.3rem;
  background: linear-gradient(135deg, #0f766e 0%, #0ea5a3 100%);
  color: white;
  border: none;
  border-radius: 0.6rem;
  font-size: 0.96rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, filter 0.15s ease;
  
  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
  
  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #d1d5db;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const DangerButton = styled(Button)`
  background: #ef4444;

  &:hover {
    filter: brightness(1.05);
  }
`;

const DeleteAccountHint = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.9rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #f8fafc;
  margin-bottom: 0.7rem;
  gap: 1rem;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const InfoValue = styled.span`
  color: #334155;
  text-align: right;
  max-width: 60%;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const SuccessAlert = styled(Alert)`
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
`;

const ErrorAlert = styled(Alert)`
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  white-space: pre-line;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ShareHint = styled.p`
  margin-top: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const WalletBanner = styled.div`
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #5eead4 100%);
  color: #fff;
  padding: 1.5rem 1.75rem;
`;

const WalletHeading = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 1rem;
  color: #fff;
`;

const WalletBalanceBig = styled.div`
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const WalletUnitLabel = styled.div`
  font-size: 0.95rem;
  opacity: 0.92;
  margin-top: 0.25rem;
`;

const WalletActivityHeading = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 1.25rem;
  opacity: 0.95;
`;

const WalletActivityList = styled.ul`
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
`;

const WalletActivityItem = styled.li`
  font-size: 0.8125rem;
  opacity: 0.95;
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  &:last-child {
    border-bottom: none;
  }
`;

const WalletFooterLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: underline;
`;

const AskFleaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1.5rem;
  background-color: #25D366;
  color: white;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s ease, transform 0.1s ease;
  
  &:hover {
    background-color: #1da851;
    color: white;
    transform: translateY(-1px);
  }

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
`;

const AskFleaHint = styled.p`
  margin-top: 0.75rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const AvatarCircle = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  border: 3px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AvatarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.75rem;
`;

const AvatarButton = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #d1d5db;

  &:hover {
    background: #f1f5f9;
  }

  input {
    display: none;
  }
`;

const AvatarRemoveButton = styled.button`
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #b91c1c;
  border: 1px solid #fecaca;

  &:hover {
    background: #fef2f2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SocialStatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
`;

const SocialStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 72px;
`;

const SocialStatButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 72px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;

  &:hover span:last-child {
    color: var(--primary-color);
  }
`;

const SocialStatNumber = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
`;

const SocialStatLabel = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.15rem;
`;

const FollowingModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const FollowingModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
`;

const FollowingModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
`;

const FollowingModalTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
`;

const FollowingModalClose = styled.button`
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
`;

const FollowingModalBody = styled.div`
  overflow-y: auto;
  padding: 0.75rem 1rem 1rem;
`;

const FollowingListItem = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.85rem 0.75rem;
  border-radius: 0.5rem;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: #f8fafc;
  }
`;

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const { t, i18n } = useTranslation();
  
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState('');
  
  const [profileData, setProfileData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    shipping_address: '',
    latitude: undefined,
    longitude: undefined,
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [displayShippingAddress, setDisplayShippingAddress] = useState(t('profile.notSet'));
  const [followingModalOpen, setFollowingModalOpen] = useState(false);
  const [followingList, setFollowingList] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);

  const followersCount = Number(user?.followers_count) || 0;
  const followingCount = Number(user?.following_count) || 0;

  const openFollowingModal = async () => {
    const username = user?.username;
    if (!username) return;
    setFollowingModalOpen(true);
    setFollowingLoading(true);
    try {
      const list = await authAPI.getFollowing(username);
      setFollowingList(Array.isArray(list) ? list : []);
    } catch {
      setFollowingList([]);
      setMessage({ type: 'error', text: t('profile.errors.loadFollowing') });
    } finally {
      setFollowingLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserProfile());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    let cancelled = false;
    const resolveShippingAddress = async () => {
      const rawAddress = user?.shipping_address || '';
      const coords = parseCoordinateAddress(rawAddress);
      if (!coords) {
        setDisplayShippingAddress(rawAddress || t('profile.notSet'));
        return;
      }
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
      const formatted = await reverseGeocodeAddress(coords, apiKey);
      if (cancelled) return;
      setDisplayShippingAddress(formatted || t('profile.addressAvailableOnMap'));
    };
    resolveShippingAddress();
    return () => {
      cancelled = true;
    };
  }, [user?.shipping_address, t]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setWalletLoading(true);
      setWalletError('');
      try {
        const data = await pointsAPI.getWallet();
        if (!cancelled) setWallet(data);
      } catch (err) {
        if (!cancelled) {
          setWalletError(err.response?.data?.error || err.message || t('profile.errors.loadWallet'));
        }
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (user) {
      setProfileData(profileFormFromUser(user));
    }
  }, [user]);

  const profilePictureUrl = avatarPreview || user?.profile_picture || '';
  const avatarInitial = (user?.first_name || user?.username || user?.name || user?.email || '?').charAt(0).toUpperCase();

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t('profile.errors.imageFileRequired') });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('profile.errors.imageTooLarge') });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setAvatarUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const compressed = await compressProfileImage(file);
      const response = await authAPI.uploadProfilePicture(compressed);
      dispatch({ type: 'auth/updateUser', payload: response });
      setMessage({ type: 'success', text: t('profile.success.avatarUpdated') });
    } catch (error) {
      setAvatarPreview('');
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('profile.errors.uploadAvatar'),
      });
    } finally {
      setAvatarUploading(false);
      URL.revokeObjectURL(previewUrl);
      setAvatarPreview('');
    }
  };

  const handleRemoveProfilePicture = async () => {
    setAvatarUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await authAPI.removeProfilePicture();
      dispatch({ type: 'auth/updateUser', payload: response });
      setAvatarPreview('');
      setMessage({ type: 'success', text: t('profile.success.avatarRemoved') });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('profile.errors.removeAvatar'),
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = { ...profileData };
      if (payload.latitude === undefined) delete payload.latitude;
      if (payload.longitude === undefined) delete payload.longitude;
      const response = await authAPI.updateProfile(payload);
      setMessage({ type: 'success', text: t('profile.success.profileUpdated') });
      setIsEditing(false);
      
      // Update the user in Redux store
      dispatch({ type: 'auth/updateUser', payload: response });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || t('profile.errors.updateProfile')
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    logger.log('Password change attempt:', {
      current: passwordData.current_password,
      new: passwordData.new_password,
      confirm: passwordData.confirm_password
    });
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: t('profile.errors.passwordsMismatch') });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      logger.log('Calling authAPI.changePassword...');
      const result = await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      logger.log('Password change result:', result);
      setMessage({ type: 'success', text: t('profile.success.passwordChanged') });
      setIsChangingPassword(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({
        type: 'error',
        text: error.message || t('profile.errors.changePassword'),
      });
      // Don't close the form on error, let user try again
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    if (!window.confirm(t('profile.deleteAccountConfirmPrompt'))) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await authAPI.deleteAccount({
        confirm: 'DELETE',
        password: deletePassword,
      });
      setIsDeletingAccount(false);
      setDeletePassword('');
      dispatch(logout());
      navigate('/', { replace: true });
      window.alert(t('profile.success.accountDeleted'));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || t('profile.errors.deleteAccount'),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    return new Date(dateString).toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShareCatalogue = async () => {
    const username = user?.username || profileData.username;
    if (!username) {
      setMessage({ type: 'error', text: t('profile.errors.usernameRequiredToShare') });
      return;
    }

    const shareUrl = `${window.location.origin}/u/${encodeURIComponent(username)}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: t('profile.catalogueTitle', { username }),
          text: t('profile.catalogueShareText'),
          url: shareUrl
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setMessage({ type: 'success', text: t('profile.success.catalogueCopied') });
      } else {
        setMessage({ type: 'success', text: t('profile.shareThisLink', { link: shareUrl }) });
      }
    } catch (error) {
      // User cancellation should not be treated as a hard error.
      if (error?.name !== 'AbortError') {
        setMessage({ type: 'error', text: t('profile.errors.shareCatalogue') });
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <ProfileContainer>
        <ProfileCard>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            {t('profile.loginRequired')}
          </p>
        </ProfileCard>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <AvatarSection>
        <AvatarCircle>
          {profilePictureUrl ? (
            <img src={profilePictureUrl} alt={t('profile.avatarAlt')} />
          ) : (
            avatarInitial
          )}
        </AvatarCircle>
        <AvatarActions>
          <AvatarButton>
            {avatarUploading ? t('profile.avatarUploading') : profilePictureUrl ? t('profile.changePhoto') : t('profile.uploadPhoto')}
            <input
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              disabled={avatarUploading}
            />
          </AvatarButton>
          {profilePictureUrl ? (
            <AvatarRemoveButton
              type="button"
              onClick={handleRemoveProfilePicture}
              disabled={avatarUploading}
            >
              {t('profile.removePhoto')}
            </AvatarRemoveButton>
          ) : null}
        </AvatarActions>
        <SocialStatsRow>
          <SocialStat>
            <SocialStatNumber>{followersCount}</SocialStatNumber>
            <SocialStatLabel>{t('profile.followers')}</SocialStatLabel>
          </SocialStat>
          <SocialStatButton type="button" onClick={openFollowingModal} aria-label={t('profile.viewFollowing')}>
            <SocialStatNumber>{followingCount}</SocialStatNumber>
            <SocialStatLabel>{t('profile.following')}</SocialStatLabel>
          </SocialStatButton>
        </SocialStatsRow>
      </AvatarSection>

      {followingModalOpen ? (
        <FollowingModalOverlay onClick={() => setFollowingModalOpen(false)}>
          <FollowingModalCard onClick={(e) => e.stopPropagation()}>
            <FollowingModalHeader>
              <FollowingModalTitle>{t('profile.following')}</FollowingModalTitle>
              <FollowingModalClose type="button" onClick={() => setFollowingModalOpen(false)}>
                {t('common.close')}
              </FollowingModalClose>
            </FollowingModalHeader>
            <FollowingModalBody>
              {followingLoading ? (
                <p>{t('common.loading')}</p>
              ) : followingList.length === 0 ? (
                <p>{t('profile.noFollowing')}</p>
              ) : (
                followingList.map((u) => (
                  <FollowingListItem
                    key={u.id}
                    to={`/u/${encodeURIComponent(u.username)}`}
                    onClick={() => setFollowingModalOpen(false)}
                  >
                    <strong>@{u.username}</strong>
                    <span>{u.name || t('profile.noName')}</span>
                  </FollowingListItem>
                ))
              )}
            </FollowingModalBody>
          </FollowingModalCard>
        </FollowingModalOverlay>
      ) : null}
      
      {message.text && (
        message.type === 'success' ? (
          <SuccessAlert>{message.text}</SuccessAlert>
        ) : (
          <ErrorAlert>{message.text}</ErrorAlert>
        )
      )}

      <ProfileCard style={{ padding: 0, overflow: 'hidden', marginBottom: '1.5rem' }}>
        <WalletBanner>
          <WalletHeading>{t('profile.walletTitle')}</WalletHeading>
          <WalletUnitLabel>{t('profile.walletBalanceLabel')}</WalletUnitLabel>
          {walletLoading && (
            <WalletBalanceBig>…</WalletBalanceBig>
          )}
          {!walletLoading && wallet && (
            <>
              <WalletBalanceBig>{Number(wallet.balance).toLocaleString()}</WalletBalanceBig>
              {Array.isArray(wallet.recent_activity) && wallet.recent_activity.length > 0 && (
                <>
                  <WalletActivityHeading>{t('profile.recentActivity')}</WalletActivityHeading>
                  <WalletActivityList>
                    {wallet.recent_activity.slice(0, 5).map((row) => (
                      <WalletActivityItem key={row.id}>
                        {`${row.direction === 'credit' ? '+' : '−'}${Number(row.amount)} ${t('profile.points')} · ${String(row.transaction_type || '').replace(/_/g, ' ')}${row.created_at ? ` · ${new Date(row.created_at).toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'ar-EG')}` : ''}`}
                      </WalletActivityItem>
                    ))}
                  </WalletActivityList>
                </>
              )}
              <WalletFooterLink to="/wallet-balance-and-aura">{t('profile.openWallet')}</WalletFooterLink>
              <WalletFooterLink to="/orders" style={{ marginTop: '0.75rem' }}>
                {t('profile.myOrders')}
              </WalletFooterLink>
            </>
          )}
          {!walletLoading && !wallet && walletError && (
            <WalletBalanceBig style={{ fontSize: '1rem', fontWeight: 600 }}>{walletError}</WalletBalanceBig>
          )}
        </WalletBanner>
      </ProfileCard>

      {/* Profile Information */}
      <ProfileCard>
        <SectionTitle>{t('profile.informationTitle')}</SectionTitle>
        
        {!isEditing ? (
          <>
            <InfoRow>
              <InfoLabel>{t('profile.firstName')}:</InfoLabel>
              <InfoValue>{splitUserName(user).firstName || t('profile.notSet')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.lastName')}:</InfoLabel>
              <InfoValue>{splitUserName(user).lastName || t('profile.notSet')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.username')}:</InfoLabel>
              <InfoValue>{user?.username || t('profile.notSet')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.email')}:</InfoLabel>
              <InfoValue>{user?.email || t('profile.notSet')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.phoneNumber')}:</InfoLabel>
              <InfoValue>{user?.phone_number || t('profile.notSet')}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.shippingAddress')}:</InfoLabel>
              <InfoValue>{displayShippingAddress}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.memberSince')}:</InfoLabel>
              <InfoValue>{formatDate(user?.date_joined)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('profile.lastLogin')}:</InfoLabel>
              <InfoValue>{formatDate(user?.last_login)}</InfoValue>
            </InfoRow>
            
            <ButtonGroup>
              <Button onClick={() => setIsEditing(true)}>
                {t('profile.editProfile')}
              </Button>
              <SecondaryButton as={Link} to="/my-catalogue">
                {t('profile.myItems')}
              </SecondaryButton>
              <SecondaryButton as={Link} to="/orders">
                {t('profile.myOrders')}
              </SecondaryButton>
              <SecondaryButton type="button" onClick={handleShareCatalogue}>
                {t('profile.shareCatalogue')}
              </SecondaryButton>
            </ButtonGroup>
            <ShareHint>{t('profile.catalogueHint')}</ShareHint>
          </>
        ) : (
          <Form onSubmit={handleProfileUpdate}>
            <FormGroup>
              <Label>{t('profile.firstName')}</Label>
              <Input
                type="text"
                value={profileData.first_name}
                onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                placeholder={t('profile.placeholders.firstName')}
                autoComplete="given-name"
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('profile.lastName')}</Label>
              <Input
                type="text"
                value={profileData.last_name}
                onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                placeholder={t('profile.placeholders.lastName')}
                autoComplete="family-name"
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('profile.username')}</Label>
              <Input
                type="text"
                value={profileData.username}
                onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                placeholder={t('profile.placeholders.username')}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('profile.email')}</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                placeholder={t('profile.placeholders.email')}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>{t('profile.phoneNumber')}</Label>
              <Input
                type="tel"
                value={profileData.phone_number}
                onChange={(e) => setProfileData({...profileData, phone_number: e.target.value})}
                placeholder={t('profile.placeholders.phoneNumber')}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>{t('profile.shippingAddress')}</Label>
              <TextArea
                value={profileData.shipping_address}
                onChange={(e) => setProfileData({...profileData, shipping_address: e.target.value})}
                placeholder={t('profile.placeholders.shippingAddress')}
              />
              <LocationPicker
                value={profileData.shipping_address}
                onChange={(address, coords) => setProfileData({
                  ...profileData,
                  shipping_address: address,
                  ...(coords?.latitude != null && coords?.longitude != null
                    ? { latitude: coords.latitude, longitude: coords.longitude }
                    : {}),
                })}
              />
            </FormGroup>
            
            <ButtonGroup>
              <Button type="submit" disabled={loading}>
                {loading ? <LoadingSpinner /> : t('profile.saveChanges')}
              </Button>
              <SecondaryButton 
                type="button" 
                onClick={() => {
                  setIsEditing(false);
                  setProfileData(profileFormFromUser(user));
                }}
              >
                {t('common.cancel')}
              </SecondaryButton>
            </ButtonGroup>
          </Form>
        )}
      </ProfileCard>

      {/* Change Password */}
      <ProfileCard>
        <SectionTitle>{t('profile.changePassword')}</SectionTitle>
        
        {!isChangingPassword ? (
          <Button onClick={() => setIsChangingPassword(true)}>
            {t('profile.changePassword')}
          </Button>
        ) : (
          <Form onSubmit={handlePasswordChange}>
            <FormGroup>
              <Label>{t('profile.currentPassword')}</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder={t('profile.placeholders.currentPassword')}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>{t('profile.newPassword')}</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder={t('profile.placeholders.newPassword')}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>{t('profile.confirmNewPassword')}</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder={t('profile.placeholders.confirmNewPassword')}
                required
              />
            </FormGroup>

            <PasswordRequirements>
              <strong>{t('profile.passwordRequirements.title')}</strong>
              <ul>
                <li>{t('profile.passwordRequirements.length')}</li>
              </ul>
            </PasswordRequirements>
            
            <ButtonGroup>
              <Button type="submit" disabled={loading}>
                {loading ? <LoadingSpinner /> : t('profile.changePassword')}
              </Button>
              <SecondaryButton 
                type="button" 
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
              >
                {t('common.cancel')}
              </SecondaryButton>
            </ButtonGroup>
          </Form>
        )}
      </ProfileCard>

      <ProfileCard>
        <SectionTitle>{t('profile.accountTitle')}</SectionTitle>
        <DeleteAccountHint>{t('profile.deleteAccountHint')}</DeleteAccountHint>

        {!isDeletingAccount ? (
          <DangerButton type="button" onClick={() => setIsDeletingAccount(true)}>
            {t('profile.deleteAccount')}
          </DangerButton>
        ) : (
          <Form onSubmit={handleDeleteAccount}>
            <FormGroup>
              <Label>{t('profile.currentPassword')}</Label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder={t('profile.placeholders.currentPassword')}
                required
              />
            </FormGroup>
            <DeleteAccountHint as="div">{t('profile.deleteAccountPasswordHint')}</DeleteAccountHint>
            <ButtonGroup>
              <DangerButton type="submit" disabled={loading}>
                {loading ? <LoadingSpinner /> : t('profile.deleteAccount')}
              </DangerButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setIsDeletingAccount(false);
                  setDeletePassword('');
                }}
                disabled={loading}
              >
                {t('common.cancel')}
              </SecondaryButton>
            </ButtonGroup>
          </Form>
        )}
      </ProfileCard>

      {/* Ask Flea */}
      <ProfileCard>
        <SectionTitle>{t('profile.needHelp')}</SectionTitle>
        <AskFleaButton
          href="https://wa.me/201040895962"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L.057 23.545a.5.5 0 0 0 .612.612l5.7-1.471A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.68-.513-5.21-1.408l-.374-.223-3.884 1.002 1.002-3.884-.223-.374A9.943 9.943 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
          </svg>
          {t('profile.askFlea')}
        </AskFleaButton>
        <AskFleaHint>{t('profile.askFleaHint')}</AskFleaHint>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default ProfilePage;
